// KAN-9：「檢查已結束」跨頁 / 跨分頁通知頻道。
//
// 為什麼不用 Vuex state + watch：
//   EVAS 的檢查頁（/examinations/:id/...）與 Home（/home）是**頂層互斥路由**，
//   且全 client 無 keep-alive。發「結束」訊號時使用者正在檢查頁，Home 元件早已銷毀、
//   watcher 根本不存在；重進 Home 時 watcher 才新建、又不會回溯舊值 → Vuex+watch 是死碼。
//   詳見知識卡 vuex-signal-watcher-requires-co-mounted-consumer。
//
// 機制：BroadcastChannel（主）+ localStorage storage event（**僅無 BroadcastChannel 時**的 fallback）。
//   兩者語義都是「送給其他 document / 分頁」（不會回送自己），正好對應
//   「Home 在別的分頁或稍後長駐時要即時刷新」；同一分頁「結束→返回 Home」則本來就靠
//   Home created() 的既有 refetch（前提是 status 寫入已 await 落地，見 Webrtc finish）。
//   A6「emit 事件 + Home 監聽 refetch」：postMessage = 事件、subscribe → refetch。

const CHANNEL_NAME = 'evas-examination-finished'
const STORAGE_KEY = 'evas:examination-finished'
const MESSAGE_TYPE = 'examination-finished'

let sharedChannel = null

// 惰性取得 BroadcastChannel 單例；瀏覽器不支援時回傳 null（走 storage fallback）
function getChannel() {
  if (sharedChannel === null && typeof BroadcastChannel !== 'undefined') {
    sharedChannel = new BroadcastChannel(CHANNEL_NAME)
  }
  return sharedChannel
}

// 發布「檢查已結束」事件（結束檢查成功、且 status 寫入落地後呼叫）
export function publishExaminationFinished(payload = {}) {
  const message = { type: MESSAGE_TYPE, at: Date.now(), ...payload }
  const channel = getChannel()
  if (channel) {
    // 有 BroadcastChannel 就只用它；發布端與訂閱端在同一瀏覽器、支援度必一致，不會漏訊。
    // 不可同時再寫 localStorage，否則另一分頁會同時收到 message + storage 兩次 → refetch 跑兩次。
    channel.postMessage(message)
  } else {
    // fallback：僅在**無** BroadcastChannel 的瀏覽器才用 localStorage 觸發跨分頁 storage 事件；
    // 每次寫入值都必須不同（帶時間戳）才會觸發 storage 事件
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(message))
    } catch (e) {
      // localStorage 不可用（無痕 / 配額）時靜默略過；同分頁仍有 Home created() refetch 保底
    }
  }
}

// 訂閱「檢查已結束」事件，回傳解除訂閱函式（Home 掛載時訂閱、銷毀時解除）
export function subscribeExaminationFinished(handler) {
  const channel = getChannel()

  const onBroadcast = (event) => {
    if (event && event.data && event.data.type === MESSAGE_TYPE) handler(event.data)
  }
  const onStorage = (event) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return
    try {
      const data = JSON.parse(event.newValue)
      if (data && data.type === MESSAGE_TYPE) handler(data)
    } catch (e) {
      // 壞掉的 payload 忽略
    }
  }

  if (channel) channel.addEventListener('message', onBroadcast)
  // storage 事件同時掛著當 BroadcastChannel fallback（不支援的瀏覽器只會收到 storage）
  window.addEventListener('storage', onStorage)

  return function unsubscribe() {
    if (channel) channel.removeEventListener('message', onBroadcast)
    window.removeEventListener('storage', onStorage)
  }
}
